import { Injectable } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of } from 'rxjs';
import {HeatConsumption, HeatConsumptionResponse} from '../models/household-data';
import { mapToHeatConsumptionResponse } from '../mappers/household-data.mapper';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class HouseholdService {
  private readonly apiUrl = `${environment.apiBaseUrl}/HouseholdView`;

  readonly houses$ = new BehaviorSubject<string[]>([]);
  readonly selectedHouseId$ = new BehaviorSubject<string | null>(null);
  readonly currentHouseholdData$ = new BehaviorSubject<HeatConsumptionResponse | null>(null);
  readonly houseLabels$ = new BehaviorSubject<{ [houseId: string]: string }>({});

  constructor(private http: HttpClient) {}

  buildLabel(data: HeatConsumptionResponse): string {
    return data.flatNumber
      ? `${data.street} ${data.houseNumber}, Wohnung ${data.flatNumber}`
      : `${data.street} ${data.houseNumber}`;
  }

  loadAllHouseLabels(houseIds: string[]): void {
    if (houseIds.length === 0) return;
    const today = new Date();
    const requests = houseIds.map((id, index) =>
      this.getHouseholdConsumption(id, today, today).pipe(
        map(data => ({ id, label: this.buildLabel(data) })),
        catchError(() => of({ id, label: `Haus ${index + 1}` }))
      )
    );
    forkJoin(requests).subscribe(results => {
      const labels = results.reduce(
        (acc, { id, label }) => ({ ...acc, [id]: label }),
        {} as { [k: string]: string }
      );
      this.houseLabels$.next(labels);
    });
  }

  getHouseholdConsumption(houseId: string, startDate: Date, endDate: Date): Observable<HeatConsumptionResponse> {
    const url = `${this.apiUrl}/HouseholdData/${houseId}`;
    const params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());

    return this.http.get<any>(url, { params }).pipe(
      map(mapToHeatConsumptionResponse)
    );
  }

  // Get all houses for a user
  getHousesByUserId(): Observable<string[]> {
    const url = `${this.apiUrl}/Houses`;
    return this.http.get<string[]>(url);
  }

  getEnvironmentTemperature(houseId: string, startDate: Date, endDate: Date): Observable<HeatConsumption> {
    const url = `${this.apiUrl}/EnvironmentTemperature/${houseId}`;
    const params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());

    return this.http.get<any>(url, { params }).pipe(
      map(raw => ({
        pointsFrequency: raw.pointsFrequency,
        telemetry: (raw.telemetry ?? []).map((t: any) => ({
          name: t.name,
          secondaryName: t.secondaryName,
          lineColour: t.lineColour,
          lineId: t.lineId,
          unit: t.unit,
          measurementTypeId: t.measurementTypeId,
          timestamps: (t.timestamps ?? []).map((ts: string) => new Date(ts)),
          values: t.values ?? [],
          currentValue: t.currentValue,
        }))
      }))
    );
  }
}
